import { Button, Checkbox, Drawer, Form, Input, Space } from "antd";
import React from "react";
import { connect } from "react-redux";
import {
  createInventoryCustomer,
  editInventoryCustomer,
} from "../../../../../api/inventory.api";
import styles from "./form.module.scss";

const AddCustomerDrawer = ({
  open,
  refresh,
  onClose,
  item = null,
  edit = false,
  settings,
}) => {
  const [form] = Form.useForm();
  const [hideShipping, setHideShipping] = React.useState(true);

  const submit = React.useCallback(
    async (values) => {
      try {
        // If hideShipping is true, then copy the billing address to shipping address
        if (hideShipping) {
          values.shipping_address = values.billing_address;
          values.shipping_city = values.billing_city;
          values.shipping_state = values.billing_state;
          values.shipping_country = values.billing_country;
          values.shipping_pincode = values.billing_pincode;
        }

        if (edit && item?.id) {
          await editInventoryCustomer(item.id, {
            ...values,
            branch: settings?.branch?.id,
          });
        } else {
          await createInventoryCustomer({
            ...values,
            branch: settings?.branch?.id,
          });
        }
        refresh();
        form.resetFields();
        onClose();
      } catch (error) {
        console.log(error);
      }
    },
    [edit, form, hideShipping, item?.id, onClose, refresh, settings?.branch?.id]
  );

  React.useEffect(() => {
    if (edit && item) {
      form.setFieldsValue(item);
    }
  }, [edit, item]);

  React.useEffect(() => {
    form.setFieldsValue({
      copy_billing_address: hideShipping,
    });
  }, [form, hideShipping]);

  const handleCopyChange = React.useCallback(
    (e) => {
      if (e.target.checked) {
        form.setFieldsValue({
          shipping_address: form.getFieldValue("billing_address"),
          shipping_city: form.getFieldValue("billing_city"),
          shipping_state: form.getFieldValue("billing_state"),
          shipping_country: form.getFieldValue("billing_country"),
          shipping_pincode: form.getFieldValue("billing_pincode"),
        });
        setHideShipping(true);
      } else {
        form.setFieldsValue({
          shipping_address: "",
          shipping_city: "",
          shipping_state: "",
          shipping_country: "",
          shipping_pincode: "",
        });
        setHideShipping(false);
      }
    },
    [form]
  );

  const handleBillingInputChange = React.useCallback(
    (field) => (e) => {
      if (form.getFieldValue("copy_billing_address")) {
        // Get the field value from the event
        const fieldValue = e.target.value;
        // Get the field name from the event
        const shippingFieldName = field.replace("billing", "shipping");
        form.setFieldsValue({
          [shippingFieldName]: fieldValue,
        });
      }
    },
    [form]
  );

  return (
    <Drawer
      title="Add new customer"
      visible={open}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={form.submit}>
            Save
          </Button>
        </Space>
      }
      width={500}
      onClose={onClose}
    >
      <Form
        layout="vertical"
        onFinish={submit}
        form={form}
        className={styles.form}
      >
        <Form.Item
          className={styles.spanner}
          name="customer_name"
          label="Customer Name"
          rules={[
            {
              required: true,
              message: "Please input customer name",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="contact"
          label="Contact"
          rules={[
            {
              required: true,
              message: "Please input contact",
            },
            {
              pattern: /^[0-9]{10}$/,
              message: "Please input valid phone number",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            {
              type: "email",
              message: "Please input valid email",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="gstin" label="GSTIN" className={styles.spanner}>
          <Input />
        </Form.Item>
        <div className={`${styles.spanner} ${styles.sectionTitle}`}>
          Billing Address
        </div>
        <Form.Item
          name="billing_address"
          label="Billing Address"
          className={styles.spanner}
        >
          <Input.TextArea
            onChange={handleBillingInputChange("billing_address")}
          />
        </Form.Item>
        <Form.Item name="billing_city" label="Billing City">
          <Input onChange={handleBillingInputChange("billing_city")} />
        </Form.Item>
        <Form.Item name="billing_state" label="Billing State">
          <Input onChange={handleBillingInputChange("billing_state")} />
        </Form.Item>
        <Form.Item name="billing_country" label="Billing Country">
          <Input onChange={handleBillingInputChange("billing_country")} />
        </Form.Item>
        <Form.Item name="billing_pincode" label="Billing Pincode">
          <Input onChange={handleBillingInputChange("billing_pincode")} />
        </Form.Item>
        <div className={`${styles.spanner} ${styles.copyCheckboxContainer}`}>
          <Form.Item
            name="copy_billing_address"
            valuePropName="checked"
            className={styles.copyCheckbox}
          >
            <Checkbox onChange={handleCopyChange}>
              Shipping address same as billing address
            </Checkbox>
          </Form.Item>
        </div>
        <div
          className={`${styles.spanner} ${styles.sectionTitle}`}
          style={{ display: hideShipping ? "none" : "block" }}
        >
          Shipping Address
        </div>
        <Form.Item
          name="shipping_address"
          label="Shipping Address"
          className={styles.spanner}
          style={{ display: hideShipping ? "none" : "block" }}
        >
          <Input.TextArea disabled={hideShipping} />
        </Form.Item>
        <Form.Item
          name="shipping_city"
          label="Shipping City"
          style={{ display: hideShipping ? "none" : "block" }}
        >
          <Input disabled={hideShipping} />
        </Form.Item>
        <Form.Item
          name="shipping_state"
          label="Shipping State"
          style={{ display: hideShipping ? "none" : "block" }}
        >
          <Input disabled={hideShipping} />
        </Form.Item>
        <Form.Item
          name="shipping_country"
          label="Shipping Country"
          style={{ display: hideShipping ? "none" : "block" }}
        >
          <Input disabled={hideShipping} />
        </Form.Item>
        <Form.Item
          name="shipping_pincode"
          label="Shipping Pincode"
          style={{ display: hideShipping ? "none" : "block" }}
        >
          <Input disabled={hideShipping} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

const mapStateToProps = ({ dispatch, settings }) => ({
  dispatch,
  settings,
});

export default connect(mapStateToProps)(AddCustomerDrawer);
